"use client";

import { c as _c } from "react/compiler-runtime";
import type { MagnusClient } from "@bg-bgi/magnus-sdk";
import { MagnusProvider, type MagnusProviderProps, useMagnusConversation } from "@bg-bgi/magnus-sdk/react";
import { type ReactNode, useMemo } from "react";
import { ChatHydrationSkeleton, type ChicletRegistry, MagnusChat } from "../magnus-chat";
import { FloatingChatBody, FloatingChatHeader, FloatingChatMinimizeButton, FloatingChatPanel, FloatingChatTitle, FloatingChatTrigger, type FloatingChatTriggerBehavior } from "./floating-chat";
import { type FloatingChatInsets, type FloatingChatPresentation, FloatingChatRoot, type FloatingChatRootProps, type FloatingChatSide, FloatingChatSourcesProvider } from "./floating-chat-context";
import type { FloatingChatTriggerAppearance } from "./floating-launcher-appearance";
import { createFloatingUrlPersistence, type FloatingUrlPersistence } from "./floating-persistence";
import { useMagnusNavigation } from "./host";
import { MagnusLogo } from "./magnus-logo";

/**
 * Presentation policy for {@link MagnusFloatingChat}.
 *
 * - `"auto"` (default) — today's behavior: floating by default, the user can
 *   switch layouts via the menu, and the choice persists.
 * - A presentation string — locked: the wrapper passes a controlled
 *   `presentation` to the root, the layout menu hides, and a stale persisted
 *   layout is ignored.
 * - `{ default, allow? }` — defaulted-but-switchable: opens as `default`,
 *   and the layout menu offers only `allow` (plus `default`).
 */
export type MagnusFloatingChatMode = "auto" | FloatingChatPresentation | {
  default: FloatingChatPresentation;
  allow?: FloatingChatPresentation[];
};
export interface MagnusFloatingChatProps {
  /**
   * Which built-in Magnus tools to expose. Forwarded to `MagnusProvider`;
   * defaults to `"all"`.
   */
  builtInTools?: MagnusProviderProps["builtInTools"];
  /** Host chiclets, merged over (and overriding) the defaults in the transcript. */
  chiclets?: ChicletRegistry;
  /**
   * The Magnus client (`createMagnus(...)`). Omit while the app is not
   * ready to talk to Magnus (signed out, unconfigured) — the shell chrome
   * still renders and `fallback` fills the panel body.
   */
  client?: MagnusClient;
  /** Uncontrolled initial open state. Defaults to closed. */
  defaultOpen?: boolean;
  /** Close the floating popup on outside click. Defaults to `true`. */
  dismissOnOutsideClick?: boolean;
  /**
   * Let the user move the launcher to any corner, by dragging it or from the
   * layout menu; the panel follows it and `persistence` remembers the corner.
   * Defaults to `false`.
   */
  draggable?: boolean;
  /** Transcript empty state. Defaults to a logo + greeting hero. */
  emptyState?: ReactNode;
  /** Panel body when `client` is absent (sign-in prompt, setup notice). */
  fallback?: ReactNode;
  /** Host chrome (a top bar, a bottom tab bar) the launcher and panel clear. */
  insets?: FloatingChatInsets;
  /** Presentation policy. Defaults to `"auto"`. */
  mode?: MagnusFloatingChatMode;
  /** Intercept layout-menu choices; return `false` to suppress the switch. */
  onLayoutSelect?: (presentation: FloatingChatPresentation) => boolean | undefined;
  /**
   * Fires on every open/close — the built-in launcher, minimize, Escape,
   * outside click, and the persisted open flag restored on mount. Pass with
   * `open` to own the state; pass alone to observe it.
   */
  onOpenChange?: (open: boolean) => void;
  /** Offer "Open in chat" in place of the full-screen overlay. */
  onOpenInChat?: () => void;
  /**
   * Controlled open state. Own it when the toggle lives in host chrome
   * outside this component (a nav "Copilot" button) — `useFloatingChat()`
   * only works inside the shell, which this component mounts internally:
   *
   * ```tsx
   * const [open, setOpen] = useState(false);
   * <TopNavButton onClick={() => setOpen((v) => !v)} />
   * <MagnusFloatingChat onOpenChange={setOpen} open={open} trigger={false} />
   * ```
   *
   * The persistence seam still works: a persisted open flag arrives through
   * `onOpenChange` on mount, and your changes are written back.
   */
  open?: boolean;
  /**
   * Where shell + chat state survive a reload. Defaults to
   * `createFloatingUrlPersistence()` — open/layout in `?magnus=`, layout
   * preference in localStorage, active chat in `?mchat=`. Pass your own
   * (e.g. a router-bound seam) or `null` to disable persistence.
   */
  persistence?: FloatingUrlPersistence | null;
  /** Composer placeholder text. */
  placeholder?: string;
  /** Corner the launcher and panel anchor to. */
  side?: FloatingChatSide;
  /** Forwarded to `MagnusProvider`. Defaults to `"embedded"`. */
  surface?: MagnusProviderProps["surface"];
  /** Forwarded to `MagnusProvider` — freeform host context sent per turn. */
  surfaceContext?: Record<string, unknown>;
  /**
   * Forwarded to `MagnusProvider` — stable instruction appendix for this
   * surface, sent on each turn. Server-side it is only honored for surfaces
   * configured to allow it; per-view data belongs in `surfaceContext`.
   */
  surfacePromptAppendix?: MagnusProviderProps["surfacePromptAppendix"];
  /** Client-side tools, forwarded to `MagnusProvider`. */
  tools?: MagnusProviderProps["tools"];
  /**
   * The launcher. Defaults to the corner FAB
   * (`<FloatingChatTrigger behavior={triggerBehavior} />`). Pass `false` or
   * `null` to render none — for hosts whose own chrome owns the toggle (see
   * `open`) — or a node to replace it. A node renders inside the shell, so it
   * can call `useFloatingChat()`.
   */
  trigger?: ReactNode | false;
  /**
   * Material of the default launcher. Defaults to `"solid"`.
   */
  triggerAppearance?: FloatingChatTriggerAppearance;
  /**
   * How the launcher behaves while the panel is open. Defaults to
   * `"hide-when-open"` (the FAB yields to the panel); `"persistent"` keeps
   * it visible as a toggle — the nav-button model `MagnusSidebarChat`
   * defaults to.
   */
  triggerBehavior?: FloatingChatTriggerBehavior;
  /** Forwarded to `MagnusProvider` (see its docs). */
  viewerId?: string;
}

/**
 * Root props a `mode` policy resolves to. Locked (string) modes drive a
 * controlled `presentation` — the root then skips presentation hydration
 * from the persistence seam, so a stale persisted layout can't override the
 * lock (open-state persistence keeps working).
 */
type ResolvedMode = {
  allowedPresentations?: readonly FloatingChatPresentation[];
  defaultPresentation?: FloatingChatPresentation;
  presentation?: FloatingChatPresentation;
};

/**
 * Everything this wrapper hands the shell root. `activeChatId` / `history`
 * are excluded: they come from the conversation inside the panel, via
 * `FloatingChatSourcesProvider`.
 */
type FloatingChatShellProps = Omit<FloatingChatRootProps, "activeChatId" | "children" | "history">;
function resolveMode(mode: MagnusFloatingChatMode | undefined): ResolvedMode {
  if (!mode || mode === "auto") {
    return {};
  }
  if (typeof mode === "string") {
    return {
      presentation: mode,
      allowedPresentations: [mode]
    };
  }
  if (!mode.allow) {
    return {
      defaultPresentation: mode.default
    };
  }
  return {
    defaultPresentation: mode.default,
    allowedPresentations: mode.allow.includes(mode.default) ? mode.allow : [mode.default, ...mode.allow]
  };
}
const DefaultEmptyState = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex flex-col items-center gap-3 text-center"><div className="flex size-11 items-center justify-center rounded-full border bg-card"><MagnusLogo className="size-6" /></div><h2 className="text-balance font-medium text-foreground text-xl sm:text-2xl">How can I help you today?</h2></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};

/**
 * The batteries-included Magnus floating chat (plan 019): launcher, popup /
 * docked-sidebar / fullscreen panel, default header with the conversation
 * switcher, and a fully wired `MagnusChat` — models, skills, artifacts,
 * feedback, history, reload restore — from a single component:
 *
 * ```tsx
 * <MagnusFloatingChat client={magnus} surface="embedded" />
 * ```
 *
 * Every seam stays overridable via props; drop down to the composable
 * `FloatingChat.*` parts + `useMagnusConversation` when the defaults no
 * longer fit.
 */
export function MagnusFloatingChat(t0) {
  const $ = _c(45);
  const {
    builtInTools,
    chiclets,
    client,
    defaultOpen,
    dismissOnOutsideClick,
    draggable,
    emptyState,
    fallback,
    insets,
    mode,
    onLayoutSelect,
    onOpenChange,
    onOpenInChat,
    open,
    persistence: persistenceProp,
    placeholder,
    side,
    surface: t1,
    surfaceContext,
    surfacePromptAppendix,
    tools,
    trigger,
    triggerAppearance,
    triggerBehavior,
    viewerId
  } = t0;
  const surface = t1 === undefined ? "embedded" : t1;
  let t2;
  if ($[0] !== persistenceProp) {
    t2 = persistenceProp === undefined ? createFloatingUrlPersistence() : persistenceProp ?? undefined;
    $[0] = persistenceProp;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const persistence = t2;
  let t3;
  if ($[2] !== mode) {
    t3 = resolveMode(mode);
    $[2] = mode;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const resolvedMode = t3;
  let t4;
  if ($[4] !== defaultOpen || $[5] !== dismissOnOutsideClick || $[6] !== draggable || $[7] !== insets || $[8] !== onLayoutSelect || $[9] !== onOpenChange || $[10] !== onOpenInChat || $[11] !== open || $[12] !== persistence || $[13] !== resolvedMode.allowedPresentations || $[14] !== resolvedMode.defaultPresentation || $[15] !== resolvedMode.presentation || $[16] !== side) {
    t4 = {
      allowedPresentations: resolvedMode.allowedPresentations,
      defaultOpen,
      defaultPresentation: resolvedMode.defaultPresentation,
      dismissOnOutsideClick,
      draggable,
      insets,
      onLayoutSelect,
      onOpenChange,
      onOpenInChat,
      open,
      presentation: resolvedMode.presentation,
      side,
      statePersistence: persistence
    };
    $[4] = defaultOpen;
    $[5] = dismissOnOutsideClick;
    $[6] = draggable;
    $[7] = insets;
    $[8] = onLayoutSelect;
    $[9] = onOpenChange;
    $[10] = onOpenInChat;
    $[11] = open;
    $[12] = persistence;
    $[13] = resolvedMode.allowedPresentations;
    $[14] = resolvedMode.defaultPresentation;
    $[15] = resolvedMode.presentation;
    $[16] = side;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const shellProps = t4;
  let t5;
  if ($[18] !== trigger || $[19] !== triggerAppearance || $[20] !== triggerBehavior) {
    t5 = trigger === undefined ? <FloatingChatTrigger appearance={triggerAppearance} behavior={triggerBehavior} /> : trigger;
    $[18] = trigger;
    $[19] = triggerAppearance;
    $[20] = triggerBehavior;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  const launcher = t5;
  if (!client) {
    let t6;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <FloatingChatHeader><FloatingChatTitle /><FloatingChatMinimizeButton /></FloatingChatHeader>;
      $[22] = t6;
    } else {
      t6 = $[22];
    }
    let t7;
    if ($[23] !== fallback) {
      t7 = <FloatingChatPanel>{t6}<FloatingChatBody>{fallback}</FloatingChatBody></FloatingChatPanel>;
      $[23] = fallback;
      $[24] = t7;
    } else {
      t7 = $[24];
    }
    let t8;
    if ($[25] !== launcher || $[26] !== shellProps || $[27] !== t7) {
      t8 = <FloatingChatRoot {...shellProps}>{launcher}{t7}</FloatingChatRoot>;
      $[25] = launcher;
      $[26] = shellProps;
      $[27] = t7;
      $[28] = t8;
    } else {
      t8 = $[28];
    }
    return t8;
  }
  let t6;
  if ($[29] !== chiclets || $[30] !== emptyState || $[31] !== launcher || $[32] !== persistence || $[33] !== placeholder || $[34] !== shellProps) {
    t6 = <FloatingConversation chiclets={chiclets} emptyState={emptyState} launcher={launcher} persistence={persistence} placeholder={placeholder} shellProps={shellProps} />;
    $[29] = chiclets;
    $[30] = emptyState;
    $[31] = launcher;
    $[32] = persistence;
    $[33] = placeholder;
    $[34] = shellProps;
    $[35] = t6;
  } else {
    t6 = $[35];
  }
  let t7;
  if ($[36] !== builtInTools || $[37] !== client || $[38] !== surface || $[39] !== surfaceContext || $[40] !== surfacePromptAppendix || $[41] !== t6 || $[42] !== tools || $[43] !== viewerId) {
    t7 = <MagnusProvider builtInTools={builtInTools} client={client} surface={surface} surfaceContext={surfaceContext} surfacePromptAppendix={surfacePromptAppendix} tools={tools} viewerId={viewerId}>{t6}</MagnusProvider>;
    $[36] = builtInTools;
    $[37] = client;
    $[38] = surface;
    $[39] = surfaceContext;
    $[40] = surfacePromptAppendix;
    $[41] = t6;
    $[42] = tools;
    $[43] = viewerId;
    $[44] = t7;
  } else {
    t7 = $[44];
  }
  return t7;
}

/**
 * `MagnusFloatingChat` locked to the docked sidebar with the persistent
 * nav-button trigger — the "always a side panel, toggled by an always-visible
 * Magnus button" pattern:
 *
 * ```tsx
 * <MagnusSidebarChat client={magnus} surface="embedded" />
 * ```
 *
 * Exactly `<MagnusFloatingChat mode="sidebar" triggerBehavior="persistent" />`;
 * pass `triggerBehavior="hide-when-open"` to opt back out of the persistent
 * trigger while keeping the locked sidebar.
 */
export function MagnusSidebarChat(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <MagnusFloatingChat mode="sidebar" triggerBehavior="persistent" {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function FloatingConversation(t0) {
  const $ = _c(9);
  const {
    chiclets,
    emptyState,
    launcher,
    persistence,
    placeholder,
    shellProps
  } = t0;
  let t1;
  if ($[0] !== chiclets || $[1] !== emptyState || $[2] !== persistence || $[3] !== placeholder) {
    t1 = <FloatingChatPanel><FloatingConversationPanel chiclets={chiclets} emptyState={emptyState} persistence={persistence} placeholder={placeholder} /></FloatingChatPanel>;
    $[0] = chiclets;
    $[1] = emptyState;
    $[2] = persistence;
    $[3] = placeholder;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== launcher || $[6] !== shellProps || $[7] !== t1) {
    t2 = <FloatingChatRoot {...shellProps}>{launcher}{t1}</FloatingChatRoot>;
    $[5] = launcher;
    $[6] = shellProps;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}

/**
 * Owns the conversation (`useMagnusConversation`) and injects it into the
 * shell via a sources overlay. This MUST render inside the panel: the panel
 * portals to `document.body` and mounts a commit after the shell, so its
 * fibers stay live under React 19.2 StrictMode + a Suspense boundary in the
 * host subtree (e.g. the host's `next/dynamic`). Above the root, the same
 * hook's store subscription can be bound to a discarded fiber and go
 * permanently silent (facebook/react#35550) — selection then never remounts
 * the transcript, and generated titles never reach the header.
 */
function FloatingConversationPanel(t0) {
  const $ = _c(15);
  const {
    chiclets,
    emptyState,
    persistence,
    placeholder
  } = t0;
  let t1;
  if ($[0] !== persistence) {
    t1 = {
      persistence
    };
    $[0] = persistence;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const conversation = useMagnusConversation(t1);
  const {
    Link
  } = useMagnusNavigation();
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <FloatingChatHeader />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== Link || $[4] !== chiclets || $[5] !== conversation.chatProps || $[6] !== conversation.loading || $[7] !== conversation.sessionKey || $[8] !== emptyState || $[9] !== placeholder) {
    t3 = <FloatingChatBody>{conversation.loading ? <ChatHydrationSkeleton className="h-full" /> : <MagnusChat chiclets={chiclets} className="h-full" emptyState={emptyState ?? <DefaultEmptyState />} key={conversation.sessionKey} linkComponent={Link} placeholder={placeholder} {...conversation.chatProps} history={undefined} />}</FloatingChatBody>;
    $[3] = Link;
    $[4] = chiclets;
    $[5] = conversation.chatProps;
    $[6] = conversation.loading;
    $[7] = conversation.sessionKey;
    $[8] = emptyState;
    $[9] = placeholder;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== conversation.chatId || $[12] !== conversation.history || $[13] !== t3) {
    t4 = <FloatingChatSourcesProvider activeChatId={conversation.chatId} history={conversation.history}>{t2}{t3}</FloatingChatSourcesProvider>;
    $[11] = conversation.chatId;
    $[12] = conversation.history;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
}